import React, { useEffect, useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import {
  FaSearch,
  FaFileAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import Loader from "../loader/Loader";
import axios from "axios";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";


const PlanningActions = ({
  onScheduleProduction,
  onAdjustTimeline,
  onExpediteMaterials,
  onViewPlanningBoard,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [planing, setPlaning] = useState({});
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [behindSchedule, setBehindSchedule] = useState([]);

  /**
   * Fetch Planning Actions
   */
  const fetchPlaningactions = async () => {
    const token = getAuthToken();

    if (!token) {
      console.error("Authentication token not found");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${config.API}/manager/planningactions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.success) {
        const data = response?.data || {};

        setPlaning(data);

        setPurchaseOrders(
          Array.isArray(data?.purchaseOrdersCount)
            ? data.purchaseOrdersCount
            : []
        );

        setBehindSchedule(
          Array.isArray(data?.behindSchedule)
            ? data.behindSchedule
            : []
        );
      } else {
        setPlaning({});
        setPurchaseOrders([]);
        setBehindSchedule([]);
      }
    } catch (err) {
      console.error(
        "Error fetching planning actions:",
        err?.response?.data || err.message
      );

      setPlaning({});
      setPurchaseOrders([]);
      setBehindSchedule([]);

      if (err?.response?.status === 401) {
        console.error("Unauthorized. Please login again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch API when component loads
   */
  useEffect(() => {
    fetchPlaningactions();
  }, []);

  /**
   * Search Purchase Orders
   */
  const filteredPOs = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) {
      return purchaseOrders;
    }

    return purchaseOrders.filter((po) => {
      const searchableFields = [
        // Required fields
        po?.product_code,
        po?.product_name,
        po?.order_status,
        po?.entry_type,
        po?.entryType,
        po?.po_type,
        po?.poType,
        po?.po_number,

        // Additional fields
        po?.order_number,
        po?.status,
        po?.type,
        po?.customer_name,
        po?.vendor_name,
      ];

      return searchableFields.some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(search)
      );
    });
  }, [purchaseOrders, searchTerm]);

  return (
    <div className="card font_family h-100">
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Header */}
          <strong className="p-3 fs-4">
            Planning Actions
          </strong>

          {/* Search */}
          <div className="px-3">
            <div className="input-group shadow-sm rounded-pill overflow-hidden">
              <input
                type="text"
                className="form-control py-2 px-3"
                placeholder="Search Product Code, Name, Status, Entry Type, PO Type, PO Number..."
                aria-label="Search purchase orders"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <button
                className="btn btn-primary px-4 search-btn"
                type="button"
              >
                <FaSearch />
              </button>
            </div>
          </div>

          {/* New Product Requirements */}
          <div className="shadow-sm mt-1 px-3">
            <div
              className="shadow-sm mt-3 overflow-auto"
              style={{
                maxHeight: "350px",
              }}
            >
              {filteredPOs.length > 0 ? (
                filteredPOs.map((purchaseOrder, index) => (
                  <div
                    key={purchaseOrder?.id || index}
                    className="mb-3 border-primary bg-primary bg-opacity-10 border-start border-4 rounded p-1"
                  >
                    <Card.Body>
                      <h6 className="text-primary fw-bold">
                        <FaFileAlt /> New Product Requirement
                        Received
                      </h6>

                      <p>
                        New order for{" "}
                        <strong>
                          {purchaseOrder?.product_name || "N/A"}
                        </strong>{" "}
                        (
                        {purchaseOrder?.quantity || 0} units) due on{" "}
                        {purchaseOrder?.delivery_date
                          ? new Date(
                              purchaseOrder.delivery_date
                            ).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "N/A"}
                        <br />

                        <span className="fw-bold">
                          Product Code:
                        </span>{" "}
                        {purchaseOrder?.product_code || "N/A"}
                        <br />

                        <span className="fw-bold">
                          PO Number:
                        </span>{" "}
                        {purchaseOrder?.po_number || "N/A"}
                        <br />

                        <span className="fw-bold">
                          Order Status:
                        </span>{" "}
                        {purchaseOrder?.order_status ||
                          purchaseOrder?.status ||
                          "N/A"}
                        <br />

                        <span className="fw-bold">
                          Entry Type:
                        </span>{" "}
                        {purchaseOrder?.entry_type ||
                          purchaseOrder?.entryType ||
                          "N/A"}
                        <br />

                        <span className="fw-bold">
                          PO Type:
                        </span>{" "}
                        {purchaseOrder?.po_type ||
                          purchaseOrder?.poType ||
                          "N/A"}
                      </p>

                      {/* Schedule Production */}
                      <Button
                        variant="dark"
                        size="sm"
                        className="me-2 mb-1"
                        onClick={() => {
                          const planId = purchaseOrder?.id;

                          if (!planId) {
                            console.error(
                              "Production Plan ID not found"
                            );
                            return;
                          }

                          onScheduleProduction?.(planId);
                        }}
                      >
                        Schedule Production
                      </Button>
                    </Card.Body>
                  </div>
                ))
              ) : (
                <div className="p-3 text-center text-muted">
                  {searchTerm
                    ? `No purchase orders found for "${searchTerm}"`
                    : "No purchase orders found"}
                </div>
              )}
            </div>
          </div>

          {/* Behind Schedule */}
          <div className="shadow-sm mt-1 px-3">
            <div
              className="shadow-sm mt-3 overflow-auto"
              style={{
                maxHeight: "350px",
              }}
            >
              {behindSchedule.length > 0 ? (
                behindSchedule.map((item, index) => (
                  <div
                    key={item?.id || index}
                    className="mb-3 border-danger bg-danger bg-opacity-10 border-start border-4 rounded p-1"
                  >
                    <Card.Body>
                      <h6 className="text-danger fw-bold">
                        <FaExclamationTriangle />{" "}
                        {item?.material_name || "Material"}{" "}
                        Behind Schedule
                      </h6>

                      <p>
                        Currently (
                        {item?.total_qty_pending || 0} units) behind
                        target. Material delay is causing production
                        slowdown.
                      </p>

                      <Button
                        variant="light"
                        size="sm"
                        className="me-2 mb-1"
                        onClick={() =>
                          onAdjustTimeline?.(item)
                        }
                      >
                        Adjust Timeline
                      </Button>

                      <Button
                        variant="dark"
                        size="sm"
                        className="me-2 mb-1"
                        onClick={() =>
                          onExpediteMaterials?.(item)
                        }
                      >
                        Expedite Materials
                      </Button>
                    </Card.Body>
                  </div>
                ))
              ) : (
                <div className="p-3 text-center text-muted">
                  No behind-schedule orders found
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PlanningActions;